import { MainApi } from 'api/MainApi'
import {
  CLEANING_TYPE_DEFAULT,
  CLEANING_TYPE_FROM_NUMBER,
  CLEANING_TYPE_TO_NUMBER,
  CleaningTypeEnum,
} from 'api/schema'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { ReactComponent as CalculateRouteSvg } from 'images/icons/calculate_route.svg'
import { ReactComponent as ZoneSvg } from 'images/icons/cleaning_zone_small.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { ReactComponent as ZoveRecordedSvg } from 'images/icons/joystick.svg'
import { ReactComponent as ZoneRecordedRouteSvg } from 'images/icons/routing.svg'
import { ReactComponent as ZoneRouteSvg } from 'images/icons/zone_route.svg'
import { cloneDeep } from 'lodash'
import SelectMissionModal from 'pages/Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectMissionModal/SelectMissionModal'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { MissionActions } from 'store/actions/mission'
import { selectHighlightedFeatureIdInList } from 'store/selectors/mission'
import { selectMissionInfo } from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { MISSION_CALCULATING_STATUSES, MISSION_ZONE_TYPES } from 'utils/constants'
import { isComboOrOmniRobotType } from 'utils/robotTypeDisplayName'
import { PATH } from 'utils/constants'
import formatDuration from 'utils/formatDuration'
import getTestId from 'utils/getTestId'
import { ICleaningZone, RootState } from 'utils/interfaces'
import { IMissionInfo } from 'utils/interfaces'

import SetOccupiedSideModal from './SetOccupiedSideModal/SetOccupiedSideModal'

import style from './ZoneItem.module.css'

const CleaningZoneItem = ({
  zone,
  number,
  hideActions,
  setCalculationMode,
}: {
  zone: ICleaningZone
  number: number
  hideActions?: boolean
  setCalculationMode: (mode: 'all' | 'single' | undefined) => void
}) => {
  const itemRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [showMissionModal, setShowMissionModal] = useState(false)
  const [showOccupiedSideModal, setShowOccupiedSideModal] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const missionInfo = useSelector(selectMissionInfo)
  const hideCleaningType = missionInfo ? !isComboOrOmniRobotType(missionInfo.robot_type) : false
  const highlightedFeatureIdInList = useSelector(selectHighlightedFeatureIdInList)
  const { cleaningZones, cleaningRoutes } = useSelector((state: RootState) => state.mission)
  const highlighted = useMemo(() => {
    return highlightedFeatureIdInList === zone.properties.zone_id || highlightedFeatureIdInList === zone.id
  }, [highlightedFeatureIdInList, zone])

  useEffect(() => {
    if (highlighted && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [highlighted])

  const zoneItemStyle = hideCleaningType ? style.zoneItemShort : style.zoneItem

  const cleaningTypeOptions: IOption[] = (
    [CleaningTypeEnum.SWEEPER, CleaningTypeEnum.SCRUBBER, CleaningTypeEnum.COMBO] as const
  ).map(type => ({ text: type, value: type }))

  const cleaningTypeFromZone =
    zone.properties.cleaning_type != null && CLEANING_TYPE_FROM_NUMBER[zone.properties.cleaning_type]
      ? CLEANING_TYPE_FROM_NUMBER[zone.properties.cleaning_type]
      : CLEANING_TYPE_DEFAULT

  const [cleaningType, setCleaningType] = useState<CleaningTypeEnum>(cleaningTypeFromZone)
  const [cleaningTypeOption, setCleaningTypeOption] = useState(
    cleaningTypeFromZone ? { text: cleaningTypeFromZone, value: cleaningTypeFromZone } : cleaningTypeOptions[0]
  )

  useEffect(() => {
    const next =
      zone.properties.cleaning_type != null && CLEANING_TYPE_FROM_NUMBER[zone.properties.cleaning_type]
        ? CLEANING_TYPE_FROM_NUMBER[zone.properties.cleaning_type]
        : CLEANING_TYPE_DEFAULT
    setCleaningType(next)
    setCleaningTypeOption({ text: next, value: next })
  }, [zone.properties.cleaning_type])

  const onZoneClicked = () => {
    dispatch(MissionActions.setHighlightedFeatureInList(zone.properties.zone_id))
    dispatch(MissionActions.setHighlightedFeature(zone))
  }

  const onEditZoneClicked = () => {
    store.dispatch(missionThunks.setEditingZone(zone))
  }

  const onEditZoneCoverageClicked = () => {
    store.dispatch(missionThunks.setEditingZoneCoverage(zone))
  }

  const onMoveZoneClicked = async (up: boolean) => {
    try {
      await store.dispatch(missionThunks.moveZone(zone, up))
    } catch (error) {
      console.error('Failed to move zone up', error)
    }
  }

  const onEditZoneRouteClicked = () => {
    store.dispatch(missionThunks.setEditingZoneRoute(zone))
  }

  const onCopyZoneClicked = () => {
    setShowMissionModal(true)
  }

  const onMissionSelected = async (mission: IMissionInfo | undefined) => {
    setShowMissionModal(false)
    if (mission) {
      try {
        await store.dispatch(missionThunks.copyCleaningZoneToMission(zone, mission.id))
        await store.dispatch(missionThunks.fetchMission(mission.id))
        navigate(`${PATH.MISSION}${mission.id}`)
      } catch (error) {
        console.error('Failed to copy zone, navigation cancelled.', error)
      }
    }
  }

  const onRemoveZoneClicked = () => {
    setShowConfirmDelete(true)
  }

  const handleDeleteConfirm = () => {
    if (zone.properties?.zone_id && zone.properties?.zone_type) {
      store.dispatch(missionThunks.deleteCleaningZone(zone))
    }
    setShowConfirmDelete(false)
  }

  const onStartCalculatingClicked = async () => {
    await store.dispatch(missionThunks.startCalculatingRoute(zone.properties?.zone_id))
    setCalculationMode('single')
  }

  const onTypeSelected = async (e: IOption) => {
    const value = e.value as CleaningTypeEnum
    const previousType = cleaningType
    const previousOption = cleaningTypeOption
    setCleaningTypeOption(e)
    setCleaningType(value)
    try {
      const updatedZone = await MainApi.changeCleaningTypeCleaningZone(
        zone.properties?.zone_id,
        CLEANING_TYPE_TO_NUMBER[value]
      )
      dispatch(MissionActions.editCleaningZone(updatedZone))
    } catch (error) {
      console.error('Failed to update cleaning type', error)
      setCleaningType(previousType)
      setCleaningTypeOption(previousOption)
    }
  }

  const reverseRoute = async () => {
    try {
      const route = cleaningRoutes.find(r => r.properties.zone_id === zone.properties.zone_id)
      if (!route || !route.geometry) return

      const newGeometry = cloneDeep(route.geometry)
      if (newGeometry.type === 'MultiLineString') {
        newGeometry.coordinates = newGeometry.coordinates.map((line: any) => [...line].reverse()).reverse()
      } else if (newGeometry.type === 'LineString') {
        // @ts-ignore
        newGeometry.coordinates = newGeometry.coordinates.reverse()
      }

      const { zone: updatedZone, cleaning_route_geometry } = await MainApi.patchCleaningZone(
        zone.properties.zone_id,
        missionInfo?.id || -1,
        zone.properties.name,
        zone.properties.description,
        zone.properties.route_id,
        undefined,
        newGeometry as any,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false
      )

      dispatch(MissionActions.editCleaningZone(updatedZone as any))
      if (cleaning_route_geometry) {
        dispatch(MissionActions.deleteRoute(zone.properties.zone_id))
        dispatch(MissionActions.addRoute(cleaning_route_geometry))
      }
      if (missionInfo?.id) {
        dispatch(missionThunks.fetchMissionBackground(missionInfo.id) as any)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const invertOrientations = async () => {
    try {
      const route = cleaningRoutes.find(r => r.properties.zone_id === zone.properties.zone_id)
      if (!route || !route.geometry) return

      const { zone: updatedZone, cleaning_route_geometry } = await MainApi.patchCleaningZone(
        zone.properties.zone_id,
        missionInfo?.id || -1,
        zone.properties.name,
        zone.properties.description,
        zone.properties.route_id,
        undefined,
        route.geometry as any,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true
      )

      dispatch(MissionActions.editCleaningZone(updatedZone as any))
      if (cleaning_route_geometry) {
        dispatch(MissionActions.deleteRoute(zone.properties.zone_id))
        dispatch(MissionActions.addRoute(cleaning_route_geometry))
      }
      if (missionInfo?.id) {
        dispatch(missionThunks.fetchMissionBackground(missionInfo.id) as any)
      }
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div
      ref={itemRef}
      className={highlighted ? clsx(zoneItemStyle, style.highlighted) : zoneItemStyle}
      onClick={onZoneClicked}
    >
      <span></span>
      <div className={style.zoneItemColSmall}>{number}</div>
      <div className={style.zoneItemColSmall}>
        {zone.properties?.is_recorded ? (
          <ZoneRecordedRouteSvg className={style.icon} />
        ) : (
          <ZoneSvg className={style.icon} />
        )}
      </div>
      <div className={style.zoneItemCol}>{zone.properties?.name}</div>
      {!hideCleaningType && (
        <div className={style.zoneItemCol}>
          <Dropdown
            currentOption={cleaningTypeOption}
            options={cleaningTypeOptions}
            onClickOption={onTypeSelected}
            useSmallDropdownStyle={true}
            canCopy={false}
          />
        </div>
      )}
      {zone.properties?.status === MISSION_CALCULATING_STATUSES.OK ? (
        <>
          <div className={style.zoneItemCol}>
            {zone.properties?.area && (
              <span className={style.bold}>
                {Math.round(zone.properties?.is_recorded ? zone.properties?.length ?? 0 : zone.properties?.length ?? 0)}
                m²
              </span>
            )}
          </div>
          <div className={style.zoneItemCol}>
            {zone.properties?.executionTime && (
              <span className={style.bold}>{formatDuration(zone.properties?.executionTime)}</span>
            )}
          </div>
        </>
      ) : (
        <div className={style.zoneItemCol} style={{ gridColumn: 'span 2' }}>
          <div className={style.marginLeft}>
            {zone.properties?.status === MISSION_CALCULATING_STATUSES.CALCULATING &&
              t('zone_details.cleaning_zone_calculating')}
            {zone.properties?.status === MISSION_CALCULATING_STATUSES.NEW && t('mission_status.new')}
            {zone.properties?.status === MISSION_CALCULATING_STATUSES.ERROR && t('mission_status.error')}
            {zone.properties?.status === MISSION_CALCULATING_STATUSES.PARTIAL_CALCULATED &&
              t('mission_status.partial_calculated')}
          </div>
        </div>
      )}
      <div className={style.zoneItemColSmall}>
        {!hideActions && (
          <ActionsMenu>
            {zone.properties?.zone_type === MISSION_ZONE_TYPES.CLEANING && (
              <ActionsMenuItem
                onClick={onStartCalculatingClicked}
                data-test-id={getTestId('calc-zone-list')}
                disabled={!(zone as ICleaningZone).properties?.readyForCalculation}
              >
                <span className={style.menuItemIcon} />
                <span data-test-id={getTestId('calc-title')}>{t('mission.calculate_route')}</span>
              </ActionsMenuItem>
            )}
            {!zone.properties?.is_recorded && (
              <ActionsMenuItem onClick={onEditZoneClicked} data-test-id={getTestId('edit-zone-list')}>
                <span className={style.menuItemIcon} />
                <span data-test-id={getTestId('edit-title')}>{t('common.edit_zone')}</span>
              </ActionsMenuItem>
            )}
            {!zone.properties?.is_recorded && (
              <ActionsMenuItem onClick={onEditZoneCoverageClicked} data-test-id={getTestId('edit-zone-coverage-list')}>
                <span className={style.menuItemIcon} />
                <span data-test-id={getTestId('edit-coverage-title')}>{t('mission.coverage_strategy')}</span>
              </ActionsMenuItem>
            )}
            <ActionsMenuItem
              onClick={onEditZoneRouteClicked}
              data-test-id={getTestId('edit-zone-route-list')}
              disabled={zone.properties?.status === MISSION_CALCULATING_STATUSES.NEW}
            >
              <span className={style.menuItemIcon} />
              <span data-test-id={getTestId('edit-title')}>{t('common.edit_route')}</span>
            </ActionsMenuItem>
            <ActionsMenuItem
              onClick={onCopyZoneClicked}
              data-test-id={getTestId('copy-zone-list')}
              disabled={zone.properties?.status === MISSION_CALCULATING_STATUSES.NEW}
            >
              <span className={style.menuItemIcon} />
              <span data-test-id={getTestId('copy-title')}>
                {!zone.properties?.is_recorded ? t('common.copy_zone') : t('common.copy_route')}
              </span>
            </ActionsMenuItem>
            {zone.properties?.is_recorded && (
              <ActionsMenuItem onClick={() => setShowOccupiedSideModal(true)}>
                <span className={style.menuItemIcon} />
                <span>{'Set occupied side'}</span>
              </ActionsMenuItem>
            )}
            {zone.properties?.is_recorded && (
              <ActionsMenuItem
                onClick={() => {
                  if (zone.properties?.zone_id) {
                    invertOrientations()
                  }
                }}
              >
                <span className={style.menuItemIcon} />
                <span>{t('route_details.invert_orientations') || 'Invert orientations'}</span>
              </ActionsMenuItem>
            )}
            {zone.properties?.is_recorded && (
              <ActionsMenuItem
                onClick={() => {
                  if (zone.properties?.zone_id) {
                    reverseRoute()
                  }
                }}
              >
                <span className={style.menuItemIcon} />
                <span>{t('route_details.reverse_direction') || 'Reverse route points'}</span>
              </ActionsMenuItem>
            )}
            {number > 1 && (
              <ActionsMenuItem onClick={() => onMoveZoneClicked(true)} data-test-id={getTestId('move-up-list')}>
                <span className={style.menuItemIcon} />
                <span data-test-id={getTestId('move-up-title')}>{t('common.move_up')}</span>
              </ActionsMenuItem>
            )}
            {number < cleaningZones.length && (
              <ActionsMenuItem onClick={() => onMoveZoneClicked(false)} data-test-id={getTestId('move-down-list')}>
                <span className={style.menuItemIcon} />
                <span data-test-id={getTestId('move-down-title')}>{t('common.move_down')}</span>
              </ActionsMenuItem>
            )}
            <ActionsMenuItem onClick={onRemoveZoneClicked} data-test-id={getTestId('remove-zone-list')}>
              <RemoveSvg className={style.menuItemIcon} />
              <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
            </ActionsMenuItem>
          </ActionsMenu>
        )}
        <SelectMissionModal
          isOpen={showMissionModal}
          robotType={missionInfo ? missionInfo.robot_type : undefined}
          onClose={() => setShowMissionModal(false)}
          onConfirmClick={onMissionSelected}
          only_ok_missions={false}
        />
        {showOccupiedSideModal && zone.properties?.zone_id && (
          <SetOccupiedSideModal
            zoneId={zone.properties.zone_id}
            isModalOpen={showOccupiedSideModal}
            closeModal={() => setShowOccupiedSideModal(false)}
          />
        )}
      </div>
      <ConfirmDialog
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirmClick={handleDeleteConfirm}
        title={
          zone.properties?.is_recorded
            ? t('mission.delete_route_confirm', { name: zone.properties?.name })
            : t('mission.delete_zone_confirm', { name: zone.properties?.name })
        }
        confirmText={t('common.delete')}
      />
    </div>
  )
}

export default CleaningZoneItem
